import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dataService from "../services/dataService";
import { useToast } from "../context/ToastContext";
import "./CRM.css";

function AddCustomer() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [whatsapp, setWhatsapp] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [address, setAddress] = useState("");
    const [taxTreatment, setTaxTreatment] = useState("VAT Registered");
    const [trn, setTrn] = useState("");
    const [placeOfSupply, setPlaceOfSupply] = useState("Dubai");
    const [paymentTerms, setPaymentTerms] = useState("Due on Receipt");
    const [subject, setSubject] = useState("");
    const [tags, setTags] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;
    const { addToast } = useToast();

    useEffect(() => {
        if (isEditMode) {
            const fetchCustomer = async () => {
                try {
                    const response = await dataService.getCustomers();
                    const customer = response.data.data.find(c => c._id === id);
                    if (customer) {
                        setName(customer.name || "");
                        setEmail(customer.email || "");
                        setPhone(customer.phone || "");
                        setWhatsapp(customer.whatsapp || "");
                        setCompanyName(customer.companyName || "");
                        setAddress(customer.address || "");
                        setTaxTreatment(customer.taxTreatment || "VAT Registered");
                        setTrn(customer.trn || "");
                        setPlaceOfSupply(customer.placeOfSupply || "Dubai");
                        setPaymentTerms(customer.paymentTerms || "Due on Receipt");
                        setSubject(customer.subject || "");
                        setTags(customer.tags ? customer.tags.join(', ') : "");
                    } else {
                        setError("Customer not found");
                    }
                } catch (err) {
                    setError("Failed to fetch customer details");
                } finally {
                    // done
                }
            };
            fetchCustomer();
        }
    }, [id, isEditMode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const customerData = {
                name,
                email,
                phone,
                whatsapp,
                companyName,
                address,
                taxTreatment,
                trn,
                placeOfSupply,
                paymentTerms,
                subject,
                tags: tags.split(',').map(t => t.trim()).filter(t => t)
            };

            if (isEditMode) {
                await dataService.updateCustomer(id, customerData);
                addToast("Customer updated successfully", "success");
            } else {
                await dataService.addCustomer(customerData);
                addToast("Customer added successfully", "success");
            }
            navigate("/crm");
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to add customer";
            setError(msg);
            addToast(msg, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="crm-screen">
            <div className="screen-header">
                <div className="header-info">
                    <h1>{isEditMode ? "Edit Customer" : "Add New Customer"}</h1>
                    <p>{isEditMode ? `Updating ${name}` : "Create a new customer profile in your database"}</p>
                </div>
            </div>

            <div className="content-card" style={{ maxWidth: '600px', margin: '32px 0' }}>
                {error && <p style={{ color: '#DC2626', marginBottom: '16px' }}>{error}</p>}
                <form onSubmit={handleSubmit} className="customer-form">
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Customer Name</label>
                        <input
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter customer name"
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Email Address</label>
                        <input
                            type="email"
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="customer@example.com"
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '32px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Phone Number</label>
                        <input
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+1 234 567 8900"
                        />
                    </div>
                    <div style={{ marginBottom: '32px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>WhatsApp Number</label>
                        <input
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={whatsapp}
                            onChange={(e) => setWhatsapp(e.target.value)}
                            placeholder="+1 234 567 8900"
                        />
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Company Name</label>
                        <input
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            placeholder="Enter company name"
                        />
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Address</label>
                        <textarea
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', minHeight: '80px' }}
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Enter customer address"
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tax Treatment</label>
                        <select
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={taxTreatment}
                            onChange={(e) => setTaxTreatment(e.target.value)}
                        >
                            <option value="VAT Registered">VAT Registered</option>
                            <option value="Non VAT Registered">Non VAT Registered</option>
                        </select>
                    </div>

                    {taxTreatment === "VAT Registered" && (
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>TRN</label>
                            <input
                                style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                                value={trn}
                                onChange={(e) => setTrn(e.target.value)}
                                placeholder="Enter 15-digit TRN"
                            />
                        </div>
                    )}

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Place of Supply</label>
                        <select
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={placeOfSupply}
                            onChange={(e) => setPlaceOfSupply(e.target.value)}
                        >
                            <option value="Dubai">Dubai</option>
                            <option value="Abu Dhabi">Abu Dhabi</option>
                            <option value="Sharjah">Sharjah</option>
                            <option value="Ajman">Ajman</option>
                            <option value="Umm Al Quwain">Umm Al Quwain</option>
                            <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                            <option value="Fujairah">Fujairah</option>
                            <option value="Outside UAE">Outside UAE</option>
                        </select>
                    </div>

                    <div style={{ marginBottom: '32px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Payment Terms</label>
                        <select
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={paymentTerms}
                            onChange={(e) => setPaymentTerms(e.target.value)}
                        >
                            <option value="Due on Receipt">Due on Receipt</option>
                            <option value="Net 15">Net 15</option>
                            <option value="Net 30">Net 30</option>
                            <option value="Net 45">Net 45</option>
                            <option value="Net 60">Net 60</option>
                        </select>
                    </div>

                    <div style={{ marginBottom: '32px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Subject</label>
                        <input
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="e.g. Inquiry about pricing"
                        />
                    </div>

                    <div style={{ marginBottom: '32px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tags (comma separated)</label>
                        <input
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px' }}
                            value={tags}
                            onChange={(e) => setTags(e.target.value)}
                            placeholder="e.g. VIP, Hot Lead"
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            type="submit"
                            className="primary-btn"
                            disabled={loading}
                            style={{ flex: 1 }}
                        >
                            {loading ? "Saving..." : (isEditMode ? "Update Customer" : "Save Customer")}
                        </button>
                        <button
                            type="button"
                            className="secondary-btn"
                            onClick={() => navigate("/crm")}
                            style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'white' }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddCustomer;